import {
  HttpClient,
  HttpEvent,
  HttpEventType,
  HttpParams,
  HttpResponse,
  HttpUploadProgressEvent,
} from '@angular/common/http';
import { inject, Inject, Injectable, Optional } from '@angular/core';

import { Store } from '@ngrx/store';
import { NEVER, Observable, pipe, UnaryFunction } from 'rxjs';
import { last, map, share, tap } from 'rxjs/operators';

import {
  APP_CONFIG,
  AUTH_METHOD,
  AUTH_TOKEN,
  COMPANY_ID,
  Helpers,
  IAppConfig,
  PATH_WHITELIST,
} from '@recruitee/common';

import { HttpActions } from './http-actions';
import { HttpApiRequestOptions, HttpRequestData } from './types';

@Injectable()
export abstract class HttpApiErrorHandlerService {
  public abstract handleError(url: string): UnaryFunction<any, any>;
}

const injectStoreOptional = () => {
  try {
    return inject(Store, { optional: true });
  } catch {
    return null;
  }
};

@Injectable()
export class HttpApiService {
  public static readonly defaultOptions: Required<HttpApiRequestOptions> = {
    skipURLBuild: false,
    skipCompany: false,
    addToken: true,
    headers: {},
  };
  public static defaultHeaders = { 'x-json-accent': 'pascal' };

  public currentCompanyId: number;
  public configApiUrl: string;
  public authToken: string;
  public appConfig = { ...(this.appConfigData || {}) };

  private store = injectStoreOptional();

  constructor(
    private http: HttpClient,
    private httpRequestData: HttpRequestData,
    private errorHandlerService: HttpApiErrorHandlerService,
    @Inject(AUTH_TOKEN) token: string,
    @Inject(COMPANY_ID) companyId: number,
    @Inject(AUTH_METHOD) public authMethod: string,
    @Inject(APP_CONFIG) private appConfigData: IAppConfig,
    @Inject(PATH_WHITELIST) @Optional() private pathWhitelist?: string[],
  ) {
    this.authToken = token;
    this.currentCompanyId = companyId;
    this.appConfig = { ...this.appConfigData };
  }

  public createSearchParams(params: Record<string, any>): HttpParams {
    let search = new HttpParams({
      encoder: {
        encodeKey: k => k,
        encodeValue: v => encodeURIComponent(v),
        decodeKey(key: string): string {
          return key;
        },
        decodeValue(value: string): string {
          return value;
        },
      },
    });

    Object.keys(params || {}).forEach(key => {
      const value = params[key];
      if (value != null && value !== '') {
        if (Array.isArray(value)) {
          value.forEach(v => (search = search.append(`${key}[]`, v)));
        } else {
          search = search.set(key, value);
        }
      }
    });

    return search;
  }

  public get<R = any>(
    path: string,
    params?: any,
    options?: HttpApiRequestOptions | null,
  ): Observable<R> {
    if (!this.isWhitelisted(path)) {
      return NEVER;
    }
    const requestOptions = this.assignDefaultOptions(options);
    const url = requestOptions.skipURLBuild
      ? path
      : this.httpRequestData.buildUrl(path, requestOptions);
    const searchParams = this.createSearchParams(params);

    return this.http
      .get(url, {
        observe: 'response',
        headers: {
          ...HttpApiService.defaultHeaders,
          ...this.httpRequestData.provideAuthHeaders(),
          ...this.httpRequestData.provideIdentityHeaders(),
          ...(options ? options.headers : {}),
        },
        params: searchParams,
      })
      .pipe(this.parseResponse(requestOptions), this.errorHandlerService.handleError(url), share());
  }

  public post<R = any>(
    path: string,
    params?: any,
    body?: object | null,
    options?: HttpApiRequestOptions | null,
    progressEmitterUuid: string | null = null,
  ): Observable<R> {
    if (!this.isWhitelisted(path)) {
      return NEVER;
    }
    const requestOptions = this.assignDefaultOptions(options);
    const url = this.httpRequestData.buildUrl(path, requestOptions);
    const searchParams = this.createSearchParams(params);
    const emitProgressEvents = !!progressEmitterUuid;

    return this.http
      .post(url, body, {
        observe: <'response'>(emitProgressEvents ? 'events' : 'response'),
        headers: {
          ...HttpApiService.defaultHeaders,
          ...this.httpRequestData.provideAuthHeaders(),
          ...this.httpRequestData.provideIdentityHeaders(),
          ...(options ? options.headers : {}),
        },
        reportProgress: emitProgressEvents,
        params: searchParams,
      })
      .pipe(
        !!progressEmitterUuid ? this.handleProgress(progressEmitterUuid) : pipe(),
        this.parseResponse(requestOptions),
        this.errorHandlerService.handleError(url),
        share(),
      );
  }

  public put<R = any>(
    path: string,
    params?: any,
    body?: object,
    options?: HttpApiRequestOptions,
  ): Observable<R> {
    if (!this.isWhitelisted(path)) {
      return NEVER;
    }
    const requestOptions = this.assignDefaultOptions(options);
    const url = this.httpRequestData.buildUrl(path, requestOptions);
    const searchParams = this.createSearchParams(params);

    return this.http
      .put(url, body, {
        observe: 'response',
        headers: {
          ...HttpApiService.defaultHeaders,
          ...this.httpRequestData.provideAuthHeaders(),
          ...this.httpRequestData.provideIdentityHeaders(),
          ...(options ? options.headers : {}),
        },
        params: searchParams,
      })
      .pipe(this.parseResponse(requestOptions), this.errorHandlerService.handleError(url), share());
  }

  public patch<R = any>(
    path: string,
    params?: any,
    body?: object | null,
    options?: HttpApiRequestOptions,
  ): Observable<R> {
    if (!this.isWhitelisted(path)) {
      return NEVER;
    }
    const requestOptions = this.assignDefaultOptions(options);
    const url = this.httpRequestData.buildUrl(path, requestOptions);
    const searchParams = this.createSearchParams(params);

    return this.http
      .patch(url, body, {
        observe: 'response',
        headers: {
          ...HttpApiService.defaultHeaders,
          ...this.httpRequestData.provideAuthHeaders(),
          ...this.httpRequestData.provideIdentityHeaders(),
          ...(options ? options.headers : {}),
        },
        params: searchParams,
      })
      .pipe(this.parseResponse(requestOptions), this.errorHandlerService.handleError(url), share());
  }

  public delete<R = any>(
    path: string,
    params?: any,
    options?: HttpApiRequestOptions,
  ): Observable<R> {
    if (!this.isWhitelisted(path)) {
      return NEVER;
    }
    const requestOptions = this.assignDefaultOptions(options);
    const url = this.httpRequestData.buildUrl(path, requestOptions);
    const searchParams = this.createSearchParams(params);

    return this.http
      .delete(url, {
        observe: 'response',
        headers: {
          ...HttpApiService.defaultHeaders,
          ...this.httpRequestData.provideAuthHeaders(),
          ...this.httpRequestData.provideIdentityHeaders(),
          ...(options ? options.headers : {}),
        },
        params: searchParams,
      })
      .pipe(this.parseResponse(requestOptions), this.errorHandlerService.handleError(url), share());
  }

  private parseResponse = (options: Required<HttpApiRequestOptions>) =>
    pipe(map((response: HttpResponse<any>) => Helpers.parseResponse(response)));

  private assignDefaultOptions(
    options?: HttpApiRequestOptions | null,
  ): Required<HttpApiRequestOptions> {
    return { ...HttpApiService.defaultOptions, ...(options || {}) };
  }

  private handleProgress = (progressEmitterUuid: string) =>
    pipe(
      tap((event: HttpEvent<any>) => {
        const progressEvent = <HttpUploadProgressEvent>event;
        if (HttpEventType.UploadProgress === progressEvent.type) {
          this.store?.dispatch(
            HttpActions.uploadProgress({
              payload: { uuid: progressEmitterUuid, event: progressEvent },
            }),
          );
        }
      }),
      last(),
    );

  private isWhitelisted(path: string): boolean {
    if (!this.pathWhitelist) return true;

    const isWhtielisted = this.pathWhitelist.includes(path);
    if (!isWhtielisted) {
      console.warn(`Path: "${path}" is not whitelisted`);
    }
    return isWhtielisted;
  }
}
